import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  OpenAiSettingsDto,
  TenantSettingsDto,
  UpdateOpenAiSettingsInput,
  UpdatePushSettingsInput,
  UpdateTenantSettingsInput,
  UpdateVerifactuSettingsInput,
  VerifactuSettingsDto,
} from '@parking/shared';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { enumLabel } from '../shared/i18n/enumLabel';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { ScrollableTabs } from '../shared/ui/ScrollableTabs';
import { SettingsCamerasPanel } from '../shared/ui/SettingsCamerasPanel';
import { SettingsSurveillancePanel } from '../shared/ui/SettingsSurveillancePanel';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

const OPENAI_MODEL_IDS = [
  'gpt-4o-mini',
  'gpt-4o',
  'gpt-4.1-mini',
  'gpt-4.1',
  'o4-mini',
  'o3-mini',
  'gpt-4o-mini-tts',
  'text-embedding-3-small',
  'whisper-1',
] as const;

export function SettingsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canRead = useAuthStore((s) => s.hasPermission('settings:read'));
  const canWrite = useAuthStore((s) => s.hasPermission('settings:write'));
  const [tab, setTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [company, setCompany] = useState<UpdateTenantSettingsInput>({});
  const [verifactu, setVerifactu] = useState<UpdateVerifactuSettingsInput>({});
  const [openai, setOpenai] = useState<UpdateOpenAiSettingsInput>({});
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [push, setPush] = useState<UpdatePushSettingsInput>({});
  const [certPem, setCertPem] = useState('');
  const [certPass, setCertPass] = useState('');

  const companyQ = useQuery({
    queryKey: ['settings-company'],
    queryFn: () => api.getCompanySettings(),
    enabled: canRead,
  });
  const verifactuQ = useQuery({
    queryKey: ['settings-verifactu'],
    queryFn: () => api.getVerifactuSettings(),
    enabled: canRead,
  });
  const openaiQ = useQuery({
    queryKey: ['settings-openai'],
    queryFn: () => api.getOpenAiSettings(),
    enabled: canRead,
  });
  const pushQ = useQuery({
    queryKey: ['settings-push'],
    queryFn: () => api.getPushSettings(),
    enabled: canRead,
  });
  const devicesQ = useQuery({
    queryKey: ['settings-push-devices'],
    queryFn: () => api.listPushDevices(),
    enabled: canRead && tab === 3,
  });
  const notifQ = useQuery({
    queryKey: ['settings-notifications'],
    queryFn: () => api.listNotifications(),
    enabled: canRead && tab === 3,
  });

  useEffect(() => {
    if (companyQ.data?.data) {
      const d: TenantSettingsDto = companyQ.data.data;
      setCompany({
        legalName: d.legalName,
        tradeName: d.tradeName,
        taxId: d.taxId,
        address: d.address,
        city: d.city,
        postalCode: d.postalCode,
        province: d.province,
        country: d.country,
        phone: d.phone,
        email: d.email,
        website: d.website,
        timezone: d.timezone,
        locale: d.locale,
        currency: d.currency,
        defaultVatRate: d.defaultVatRate,
        invoiceFooterNote: d.invoiceFooterNote,
      });
    }
  }, [companyQ.data]);

  useEffect(() => {
    if (verifactuQ.data?.data) {
      const d: VerifactuSettingsDto = verifactuQ.data.data;
      setVerifactu({
        enabled: d.enabled,
        environment: d.environment,
        nifEmisor: d.nifEmisor,
        nombreRazon: d.nombreRazon,
        softwareNif: d.softwareNif,
        softwareName: d.softwareName,
        softwareId: d.softwareId,
        softwareVersion: d.softwareVersion,
        apiBaseUrl: d.apiBaseUrl,
      });
    }
  }, [verifactuQ.data]);

  useEffect(() => {
    if (openaiQ.data?.data) {
      const d: OpenAiSettingsDto = openaiQ.data.data;
      setOpenai({
        enabled: d.enabled,
        organizationId: d.organizationId,
        projectId: d.projectId,
        baseUrl: d.baseUrl,
        defaultModel: d.defaultModel,
        temperature: d.temperature,
        maxTokens: d.maxTokens,
      });
      setOpenaiApiKey('');
    }
  }, [openaiQ.data]);

  useEffect(() => {
    if (pushQ.data?.data) {
      const d = pushQ.data.data;
      setPush({
        enabled: d.enabled,
        notifySessionOpen: d.notifySessionOpen,
        notifySessionClose: d.notifySessionClose,
        notifyInvoice: d.notifyInvoice,
        notifyPayment: d.notifyPayment,
        notifyIncident: d.notifyIncident,
        webhookUrl: d.webhookUrl,
      });
    }
  }, [pushQ.data]);

  const saveCompany = useMutation({
    mutationFn: () => api.updateCompanySettings(company),
    onSuccess: async () => {
      setOk(t('settings.saved'));
      setError(null);
      await qc.invalidateQueries({ queryKey: ['settings-company'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const saveVerifactu = useMutation({
    mutationFn: () =>
      api.updateVerifactuSettings({
        ...verifactu,
        ...(certPem ? { certificatePem: certPem } : {}),
        ...(certPass ? { certificatePassphrase: certPass } : {}),
      }),
    onSuccess: async () => {
      setOk(t('settings.saved'));
      setError(null);
      setCertPem('');
      setCertPass('');
      await qc.invalidateQueries({ queryKey: ['settings-verifactu'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const validateVerifactu = useMutation({
    mutationFn: () => api.validateVerifactuSettings(),
    onSuccess: async () => {
      setOk(t('settings.verifactu.validated'));
      setError(null);
      await qc.invalidateQueries({ queryKey: ['settings-verifactu'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const saveOpenAi = useMutation({
    mutationFn: () =>
      api.updateOpenAiSettings({
        ...openai,
        ...(openaiApiKey.trim() ? { apiKey: openaiApiKey.trim() } : {}),
      }),
    onSuccess: async () => {
      setOk(t('settings.saved'));
      setError(null);
      setOpenaiApiKey('');
      await qc.invalidateQueries({ queryKey: ['settings-openai'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const savePush = useMutation({
    mutationFn: () => api.updatePushSettings(push),
    onSuccess: async () => {
      setOk(t('settings.saved'));
      setError(null);
      await qc.invalidateQueries({ queryKey: ['settings-push'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const subscribe = useMutation({
    mutationFn: async () => {
      const publicKey = pushQ.data?.data.vapidPublicKey;
      if (!publicKey) throw new Error(t('settings.push.noVapid'));
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        throw new Error(t('settings.push.unsupported'));
      }
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') throw new Error(t('settings.push.denied'));
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const json = sub.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error(t('settings.push.subscribeFailed'));
      }
      return api.subscribePush({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        label: navigator.userAgent.slice(0, 80),
      });
    },
    onSuccess: async () => {
      setOk(t('settings.push.subscribed'));
      setError(null);
      await qc.invalidateQueries({ queryKey: ['settings-push-devices'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const testPush = useMutation({
    mutationFn: () => api.testPush(),
    onSuccess: async (res) => {
      setOk(t('settings.push.testResult', { sent: res.data.sent, failed: res.data.failed }));
      await qc.invalidateQueries({ queryKey: ['settings-notifications'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!canRead) {
    return <div className="alert alert-warning">{t('settings.forbidden')}</div>;
  }

  const deviceRows = devicesQ.data?.data ?? [];
  const notifRows = notifQ.data?.data ?? [];

  return (
    <>
{error && <div className="alert alert-danger">{error}</div>}
      {ok && <div className="alert alert-success">{ok}</div>}

      <ScrollableTabs
        activeId={String(tab)}
        onChange={(id) => setTab(Number(id))}
        items={[
          { id: '0', label: t('settings.tabs.company') },
          { id: '1', label: t('settings.tabs.verifactu') },
          { id: '2', label: t('settings.tabs.openai') },
          { id: '3', label: t('settings.tabs.push') },
          { id: '4', label: t('settings.tabs.cameras') },
          { id: '5', label: t('settings.tabs.surveillance') },
        ]}
      />

      {tab === 0 && (
        <Card title={t('settings.tabs.company')}>
          <div className="row g-3">
            {(
              [
                ['legalName', t('settings.company.legalName'), 'col-md-6'],
                ['tradeName', t('settings.company.tradeName'), 'col-md-6'],
                ['taxId', t('settings.company.taxId'), 'col-md-4'],
                ['phone', t('settings.company.phone'), 'col-md-4'],
                ['email', t('settings.company.email'), 'col-md-4'],
                ['address', t('settings.company.address'), 'col-12'],
                ['city', t('settings.company.city'), 'col-md-4'],
                ['postalCode', t('settings.company.postalCode'), 'col-md-4'],
                ['province', t('settings.company.province'), 'col-md-4'],
                ['country', t('settings.company.country'), 'col-md-4'],
                ['timezone', t('settings.company.timezone'), 'col-md-4'],
                ['locale', t('settings.company.locale'), 'col-md-4'],
                ['website', t('settings.company.website'), 'col-md-6'],
                ['currency', t('settings.company.currency'), 'col-md-3'],
              ] as const
            ).map(([key, label, col]) => (
              <div className={col} key={key}>
                <label className="form-label">{label}</label>
                <input
                  className="form-control"
                  value={(company[key] as string | null | undefined) ?? ''}
                  onChange={(e) => setCompany((c) => ({ ...c, [key]: e.target.value }))}
                  disabled={!canWrite}
                />
              </div>
            ))}
            <div className="col-md-3">
              <label className="form-label">{t('settings.company.vat')}</label>
              <input
                className="form-control"
                type="number"
                value={company.defaultVatRate ?? 21}
                onChange={(e) =>
                  setCompany((c) => ({ ...c, defaultVatRate: Number(e.target.value) }))
                }
                disabled={!canWrite}
              />
            </div>
            <div className="col-12">
              <label className="form-label">{t('settings.company.footer')}</label>
              <textarea
                className="form-control"
                rows={3}
                value={company.invoiceFooterNote ?? ''}
                onChange={(e) => setCompany((c) => ({ ...c, invoiceFooterNote: e.target.value }))}
                disabled={!canWrite}
              />
            </div>
            {canWrite && (
              <div className="col-12">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={saveCompany.isPending}
                  onClick={() => saveCompany.mutate()}
                >
                  {t('common.save')}
                </button>
              </div>
            )}
          </div>
        </Card>
      )}

      {tab === 1 && (
        <Card title={t('settings.tabs.verifactu')}>
          <div className="row g-3">
            <div className="col-12">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="verifactu-enabled"
                  checked={Boolean(verifactu.enabled)}
                  onChange={(e) => setVerifactu((v) => ({ ...v, enabled: e.target.checked }))}
                  disabled={!canWrite}
                />
                <label className="form-check-label" htmlFor="verifactu-enabled">
                  {t('settings.verifactu.enabled')}
                </label>
              </div>
              <p className="text-muted small mb-0 mt-2">
                {t('settings.verifactu.status')}:{' '}
                {enumLabel(t, 'verifactuStatus', verifactuQ.data?.data.status ?? 'DISABLED')}
                {verifactuQ.data?.data.hasCertificate
                  ? ` · ${t('settings.verifactu.hasCert')}`
                  : ` · ${t('settings.verifactu.noCert')}`}
              </p>
            </div>
            <div className="col-md-4">
              <label className="form-label">{t('settings.verifactu.environment')}</label>
              <select
                className="form-select"
                value={verifactu.environment ?? 'TEST'}
                onChange={(e) =>
                  setVerifactu((v) => ({
                    ...v,
                    environment: e.target.value as 'TEST' | 'PROD',
                  }))
                }
                disabled={!canWrite}
              >
                <option value="TEST">TEST</option>
                <option value="PROD">PROD</option>
              </select>
            </div>
            {(
              [
                ['nifEmisor', t('settings.verifactu.nif'), 'col-md-4'],
                ['softwareNif', t('settings.verifactu.softwareNif'), 'col-md-4'],
                ['nombreRazon', t('settings.verifactu.razon'), 'col-12'],
                ['softwareName', t('settings.verifactu.softwareName'), 'col-md-4'],
                ['softwareId', t('settings.verifactu.softwareId'), 'col-md-4'],
                ['softwareVersion', t('settings.verifactu.softwareVersion'), 'col-md-4'],
                ['apiBaseUrl', t('settings.verifactu.apiUrl'), 'col-12'],
              ] as const
            ).map(([key, label, col]) => (
              <div className={col} key={key}>
                <label className="form-label">{label}</label>
                <input
                  className="form-control"
                  value={(verifactu[key] as string | null | undefined) ?? ''}
                  onChange={(e) => setVerifactu((v) => ({ ...v, [key]: e.target.value }))}
                  disabled={!canWrite}
                />
              </div>
            ))}
            <div className="col-12">
              <label className="form-label">{t('settings.verifactu.certificate')}</label>
              <textarea
                className="form-control"
                rows={4}
                value={certPem}
                onChange={(e) => setCertPem(e.target.value)}
                disabled={!canWrite}
              />
              <div className="form-text">{t('settings.verifactu.certificateHint')}</div>
            </div>
            <div className="col-12">
              <label className="form-label">{t('settings.verifactu.passphrase')}</label>
              <input
                className="form-control"
                type="password"
                value={certPass}
                onChange={(e) => setCertPass(e.target.value)}
                disabled={!canWrite}
              />
            </div>
            {canWrite && (
              <div className="col-12 d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={saveVerifactu.isPending}
                  onClick={() => saveVerifactu.mutate()}
                >
                  {t('common.save')}
                </button>
                <button
                  type="button"
                  className="btn btn-light"
                  disabled={validateVerifactu.isPending}
                  onClick={() => validateVerifactu.mutate()}
                >
                  {t('settings.verifactu.validate')}
                </button>
              </div>
            )}
          </div>
        </Card>
      )}

      {tab === 2 && (
        <Card title={t('settings.tabs.openai')}>
          <div className="row g-3">
            <div className="col-12">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="openai-enabled"
                  checked={Boolean(openai.enabled)}
                  onChange={(e) => setOpenai((o) => ({ ...o, enabled: e.target.checked }))}
                  disabled={!canWrite}
                />
                <label className="form-check-label" htmlFor="openai-enabled">
                  {t('settings.openai.enabled')}
                </label>
              </div>
              <p className="text-muted small mb-0 mt-2">
                {openaiQ.data?.data.hasApiKey
                  ? t('settings.openai.hasKey')
                  : t('settings.openai.noKey')}
              </p>
            </div>
            <div className="col-md-6">
              <label className="form-label">{t('settings.openai.apiKey')}</label>
              <input
                className="form-control"
                type="password"
                autoComplete="off"
                value={openaiApiKey}
                placeholder={
                  openaiQ.data?.data.hasApiKey
                    ? t('settings.openai.apiKeyKeep')
                    : t('settings.openai.apiKeyPlaceholder')
                }
                onChange={(e) => setOpenaiApiKey(e.target.value)}
                disabled={!canWrite}
              />
              <div className="form-text">{t('settings.openai.apiKeyHint')}</div>
            </div>
            <div className="col-md-6">
              <label className="form-label">{t('settings.openai.baseUrl')}</label>
              <input
                className="form-control"
                value={openai.baseUrl ?? ''}
                onChange={(e) => setOpenai((o) => ({ ...o, baseUrl: e.target.value }))}
                disabled={!canWrite}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">{t('settings.openai.organizationId')}</label>
              <input
                className="form-control"
                value={openai.organizationId ?? ''}
                onChange={(e) => setOpenai((o) => ({ ...o, organizationId: e.target.value }))}
                disabled={!canWrite}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">{t('settings.openai.projectId')}</label>
              <input
                className="form-control"
                value={openai.projectId ?? ''}
                onChange={(e) => setOpenai((o) => ({ ...o, projectId: e.target.value }))}
                disabled={!canWrite}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">{t('settings.openai.defaultModel')}</label>
              <select
                className="form-select"
                value={openai.defaultModel ?? 'gpt-4o-mini'}
                onChange={(e) => setOpenai((o) => ({ ...o, defaultModel: e.target.value }))}
                disabled={!canWrite}
              >
                {OPENAI_MODEL_IDS.map((id) => (
                  <option key={id} value={id}>
                    {t(`settings.openai.models.${id}`)}
                  </option>
                ))}
                {openai.defaultModel &&
                !OPENAI_MODEL_IDS.includes(
                  openai.defaultModel as (typeof OPENAI_MODEL_IDS)[number],
                ) ? (
                  <option value={openai.defaultModel}>{openai.defaultModel}</option>
                ) : null}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">{t('settings.openai.temperature')}</label>
              <input
                className="form-control"
                type="number"
                min={0}
                max={2}
                step={0.1}
                value={openai.temperature ?? 0.2}
                onChange={(e) =>
                  setOpenai((o) => ({ ...o, temperature: Number(e.target.value) }))
                }
                disabled={!canWrite}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">{t('settings.openai.maxTokens')}</label>
              <input
                className="form-control"
                type="number"
                min={1}
                value={openai.maxTokens ?? ''}
                onChange={(e) =>
                  setOpenai((o) => ({
                    ...o,
                    maxTokens: e.target.value === '' ? null : Number(e.target.value),
                  }))
                }
                disabled={!canWrite}
              />
            </div>
            {canWrite && (
              <div className="col-12">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={saveOpenAi.isPending}
                  onClick={() => saveOpenAi.mutate()}
                >
                  {t('common.save')}
                </button>
              </div>
            )}
          </div>
        </Card>
      )}

      {tab === 3 && (
        <>
          <Card title={t('settings.tabs.push')} className="mb-4">
            <div className="row g-3">
              <div className="col-12">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="push-enabled"
                    checked={Boolean(push.enabled)}
                    onChange={(e) => setPush((p) => ({ ...p, enabled: e.target.checked }))}
                    disabled={!canWrite}
                  />
                  <label className="form-check-label" htmlFor="push-enabled">
                    {t('settings.push.enabled')}
                  </label>
                </div>
              </div>
              {(
                [
                  ['notifySessionOpen', t('settings.push.notifyOpen')],
                  ['notifySessionClose', t('settings.push.notifyClose')],
                  ['notifyInvoice', t('settings.push.notifyInvoice')],
                  ['notifyPayment', t('settings.push.notifyPayment')],
                  ['notifyIncident', t('settings.push.notifyIncident')],
                ] as const
              ).map(([key, label]) => (
                <div className="col-md-6 col-xl-4" key={key}>
                  <div className="form-check form-switch h-100">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`push-${key}`}
                      checked={Boolean(push[key])}
                      onChange={(e) => setPush((p) => ({ ...p, [key]: e.target.checked }))}
                      disabled={!canWrite}
                    />
                    <label className="form-check-label" htmlFor={`push-${key}`}>
                      {label}
                    </label>
                  </div>
                </div>
              ))}
              <div className="col-md-6">
                <label className="form-label">{t('settings.push.webhookUrl')}</label>
                <input
                  className="form-control"
                  value={push.webhookUrl ?? ''}
                  onChange={(e) => setPush((p) => ({ ...p, webhookUrl: e.target.value }))}
                  disabled={!canWrite}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">{t('settings.push.webhookSecret')}</label>
                <input
                  className="form-control"
                  type="password"
                  value={push.webhookSecret ?? ''}
                  onChange={(e) => setPush((p) => ({ ...p, webhookSecret: e.target.value }))}
                  disabled={!canWrite}
                />
              </div>
              {canWrite && (
                <div className="col-12 d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={savePush.isPending}
                    onClick={() => savePush.mutate()}
                  >
                    {t('common.save')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-light"
                    disabled={subscribe.isPending}
                    onClick={() => subscribe.mutate()}
                  >
                    {t('settings.push.subscribe')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-light"
                    disabled={testPush.isPending}
                    onClick={() => testPush.mutate()}
                  >
                    {t('settings.push.test')}
                  </button>
                </div>
              )}
            </div>
          </Card>

          <Card title={t('settings.push.devices')} className="mb-4">
            <ParkingDataTable
              storageKey="settings-push-devices"
              columns={[t('settings.push.label'), t('settings.push.created'), t('common.actions')]}
              refreshKey={deviceRows.map((d) => d.id).join('|')}
              actionsColumn={canWrite}
              galleryItems={deviceRows.map((d) => ({
                id: d.id,
                icon: 'bi-phone',
                iconTone: 'primary' as const,
                title: d.label ?? d.endpoint.slice(0, 48),
                meta: new Date(d.createdAt).toLocaleString(),
                actions: canWrite ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-danger"
                    onClick={async () => {
                      await api.unsubscribePush(d.id);
                      await qc.invalidateQueries({ queryKey: ['settings-push-devices'] });
                    }}
                  >
                    <i className="bi bi-trash" />
                  </button>
                ) : undefined,
              }))}
            >
              {deviceRows.map((d) => (
                <tr key={d.id}>
                  <td>{d.label ?? d.endpoint.slice(0, 48)}</td>
                  <td>{new Date(d.createdAt).toLocaleString()}</td>
                  <td>
                    {canWrite && (
                      <button
                        type="button"
                        className="btn btn-sm btn-icon btn-ghost-danger"
                        onClick={async () => {
                          await api.unsubscribePush(d.id);
                          await qc.invalidateQueries({ queryKey: ['settings-push-devices'] });
                        }}
                      >
                        <i className="bi bi-trash" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </ParkingDataTable>
          </Card>

          <Card title={t('settings.push.logs')}>
            <ParkingDataTable
              storageKey="settings-push-logs"
              columns={[
                t('settings.push.channel'),
                t('settings.push.titleCol'),
                t('settings.push.status'),
                t('settings.push.created'),
              ]}
              refreshKey={notifRows.map((n) => n.id).join('|')}
              order={[[3, 'desc']]}
              actionsColumn={false}
              galleryItems={notifRows.map((n) => ({
                id: n.id,
                icon: 'bi-bell',
                iconTone: 'info' as const,
                title: n.title,
                subtitle: n.body,
                meta: `${n.channel} · ${new Date(n.createdAt).toLocaleString()}`,
                badge: <span className="badge text-bg-secondary">{n.status}</span>,
              }))}
            >
              {notifRows.map((n) => (
                <tr key={n.id}>
                  <td>{n.channel}</td>
                  <td>
                    <div className="fw-semibold">{n.title}</div>
                    <div className="text-muted small">{n.body}</div>
                  </td>
                  <td>{n.status}</td>
                  <td>{new Date(n.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </ParkingDataTable>
          </Card>
        </>
      )}

      {tab === 4 && (
        <Card title={t('settings.tabs.cameras')}>
          <SettingsCamerasPanel canWrite={canWrite} />
        </Card>
      )}

      {tab === 5 && (
        <Card title={t('settings.tabs.surveillance')}>
          <SettingsSurveillancePanel canWrite={canWrite} />
        </Card>
      )}
    </>
  );
}
